/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText);

import Swiper from "swiper";
import initGalleryLightbox from "./gallery-lightbox";
import {
	Autoplay,
	// Thumbs,
	EffectFade,
	Navigation,
	Pagination,
	A11y,
	//Scrollbar,
} from "swiper/modules";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	gsapPreloader();
	scrollDetection(160);
	menuBurger();
	syncMobileMenuOffset();
	menuDropdown();
	//initBanner();
	initDesktopHeaderScroll();
	//bannerToMarquee();
	//popinInit();
	toggleInit();
	accordionInit();
	swiperInit();
	initGalleryLightbox(scrollEnabled);
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	//bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Scroll vers une ancre
function scrollToAnchor(scrollOffset = 100) {

	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
}

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 0) => {
	// Safari peut dépasser les limites de la page pendant le rebond.
	const getScrollPosition = () => {
		const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
		return Math.min(maxScroll, Math.max(0, window.scrollY));
	};
	let position = getScrollPosition();

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		const scroll = getScrollPosition();

		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				// Effet page-scrolling désactivé ; décommenter pour le réactiver.
				// document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}

		// Scroll vers le haut
		else if (scroll < position) {
			// Réafficher la navbar dans le dernier quart d'écran avant le haut.
			if (scroll <= window.innerHeight / 4) {
				document.body.classList.remove("page-scrolling");
			}
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}

		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}

		position = scroll;
	});
};

// Le header accompagne le scroll jusqu'à ce que la navigation atteigne le haut.
function initDesktopHeaderScroll() {
	const header = document.querySelector(".page__header");
	const navigation = header?.querySelector(".header__navbar__nav");
	if (!navigation || document.body.classList.contains("elementor-editor-active")) return;

	gsap.matchMedia().add("(min-width: 1080.02px)", () => {
		// Mesure relative, indépendante de la translation déjà appliquée au header.
		const getTravel = () => Math.max(0,
			navigation.getBoundingClientRect().top - header.getBoundingClientRect().top
			+ (parseFloat(getComputedStyle(header).top) || 0)
		);
		const syncFixedNavigation = (trigger) => {
			navigation.classList.toggle("is-fixed", trigger.progress >= 1);
		};

		gsap.fromTo(header, { y: 0 }, {
			y: () => -getTravel(),
			ease: "none",
			scrollTrigger: {
				start: 0,
				end: () => Math.max(1, getTravel()),
				scrub: true,
				invalidateOnRefresh: true,
				onUpdate: syncFixedNavigation,
				onRefresh: syncFixedNavigation,
			},
		});
		// matchMedia restaure les styles et supprime le trigger sur mobile.
		return () => navigation.classList.remove("is-fixed");
	});
}

// Gestion du menu burger
const menuBurger = () => {
	const headerBurgerButton = document.querySelector("#page-header-burger");
	const headerBurgerArea = document.querySelector("#page-header");

	if (headerBurgerButton) {
		// Fermer le menu dès que le breakpoint CSS masque le burger.
		const burgerVisibilityObserver = new ResizeObserver(() => {
			if (
				getComputedStyle(headerBurgerButton).display === "none" &&
				document.body.classList.contains("page-with-fullscreenmenu")
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			}
		});
		burgerVisibilityObserver.observe(headerBurgerButton);

		headerBurgerButton.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullscreenmenu");
			}
		});

		document.addEventListener("mouseup", (event) => {
			if (
				document.body.classList.contains("page-with-fullscreenmenu") &&
				!headerBurgerButton.contains(event.target) &&
				headerBurgerButton !== event.target &&
				!headerBurgerArea.contains(event.target) &&
				headerBurgerArea !== event.target
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			}
		});
	}
};

// Suivre la hauteur rendue de la navbar, y compris pendant ses transitions.
const syncMobileMenuOffset = () => {
	const navbar = document.querySelector(".header__navbar");
	const burger = document.querySelector("#page-header-burger");
	const wrapper = document.querySelector(".header__fullscreenmenu__wrapper");

	if (!navbar || !burger || !wrapper) return;

	let observingNavbar = false;
	const updateHeight = () => {
		if (!observingNavbar) return;
		wrapper.style.setProperty(
			"--mobile-navbar-height",
			`${navbar.getBoundingClientRect().height}px`
		);
	};
	const navbarObserver = new ResizeObserver(updateHeight);
	const burgerObserver = new ResizeObserver(() => {
		const burgerVisible = getComputedStyle(burger).display !== "none";
		if (burgerVisible === observingNavbar) return;

		observingNavbar = burgerVisible;
		if (burgerVisible) {
			navbarObserver.observe(navbar, { box: "border-box" });
			updateHeight();
		} else {
			navbarObserver.disconnect();
			wrapper.style.removeProperty("--mobile-navbar-height");
		}
	});

	// display:none declenche aussi l'observateur au changement de breakpoint.
	burgerObserver.observe(burger);
};

// Gestion des dropdowns
const menuDropdown = () => {
	document.querySelectorAll(".header__fullscreenmenu__menu > .menu__item > .menu__dropdown").forEach((dropdown, index) => {
		const item = dropdown.parentElement;
		const link = item.querySelector(".menu__link");
		dropdown.id = `mobile-submenu-${index}`;
		link.setAttribute("aria-controls", dropdown.id);
		item.classList.toggle("menu__item--active", !!dropdown.querySelector('[aria-current="page"]'));
		link.setAttribute("aria-expanded", item.classList.contains("menu__item--active"));
		link.addEventListener("click", (event) => {
			const toggleWidth = parseFloat(getComputedStyle(link).getPropertyValue("--submenu-hit-size"));
			if (!event.detail || event.clientX < link.getBoundingClientRect().right - toggleWidth) return;
			event.preventDefault();
			link.setAttribute("aria-expanded", item.classList.toggle("menu__item--active"));
		});
		link.addEventListener("keydown", (event) => {
			if (event.key !== " ") return;
			event.preventDefault();
			link.setAttribute("aria-expanded", item.classList.toggle("menu__item--active"));
		});
	});

	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		if (item.closest(".header__fullscreenmenu__menu")) return;
		item.closest(".menu__item").classList.add("menu__item--parent");

		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
				activeItem.querySelector('.menu__link[aria-expanded]')?.setAttribute("aria-expanded", "false");
			}
		});
	});
};

// Activation/désactivation de la bannière
const initBanner = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");

		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
			// Recalculer la position du header une fois sa transition terminée.
			gsap.delayedCall(0.35, () => ScrollTrigger.refresh());
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;

		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Gestions des actions des [data-toggle]
const toggleInit = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((togglelistener) => {
		const toggleTargetSelector = togglelistener.dataset.toggle;
		const toggleTarget = toggleTargetSelector
			? document.querySelector(toggleTargetSelector)
			: null;

		togglelistener.addEventListener("click", (event) => {
			if (toggleTarget) {
				// event.stopPropagation();
				toggleTarget.classList.toggle("--active");
			}
		});
	});

	// Gestionnaire pour les clics hors des éléments toggle
	document.addEventListener("click", (event) => {
		toggleListeners.forEach((togglelistener) => {
			const toggleTargetSelector = togglelistener.dataset.toggle;
			const toggleTarget = toggleTargetSelector
				? document.querySelector(toggleTargetSelector)
				: null;

			if (
				toggleTarget &&
				toggleTarget.classList.contains("--active") &&
				!toggleTarget.contains(event.target) &&
				!togglelistener.contains(event.target)
			) {
				toggleTarget.classList.remove("--active");
			}
		});
	});
};

// Gestions des actions des [data-accordion]
const accordionInit = () => {
	const accordionListeners = document.querySelectorAll("[data-accordion]");

	accordionListeners.forEach((accordionElement) => {
		accordionElement.addEventListener("click", (event) => {
			event.stopPropagation();

			// Retirer la classe "--active" à tous les accordionListeners et accordionTargets
			accordionListeners.forEach((element) => {
				if (element !== accordionElement) {
					const accordionTarget = document.getElementById(
						element.dataset.accordion
					);

					if (accordionTarget) {
						element.classList.remove("--active");
						accordionTarget.classList.remove("--active");
					}
				}
			});

			// Ajouter la classe "--active" à la cible
			if (accordionElement.dataset.accordion) {
				const currentaccordionTarget = document.getElementById(
					accordionElement.dataset.accordion
				);

				if (currentaccordionTarget) {
					accordionElement.classList.add("--active");
					currentaccordionTarget.classList.add("--active");
				}
			}
		});
	});
};

// Gestions des actions des [data-popin]
const popinInit = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;

			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`
				);

				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");

			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});

	document.addEventListener("mouseup", (event) => {
		// si une popin est ouverte
		if (
			document.querySelectorAll(".page__popin.page__popin--open").length > 0
		) {
			// clic en dehors de la zone
			if (
				!document
					.querySelector(".page__popin .popin__wrapper")
					.contains(event.target) &&
				document.querySelector(".page__popin .popin__wrapper") !== event.target
			) {
				// fermer la popin
				document.querySelectorAll(".page__popin").forEach((popin) => {
					popin.classList.remove("page__popin--open");
				});

				// mettre les vidéos des popins en pause
				const htmlVideoFrompopin =
					document.querySelectorAll(".page__popin video");

				if (htmlVideoFrompopin.length > 0) {
					htmlVideoFrompopin.forEach((popinVideo) => {
						popinVideo.pause();
					});
				}
			}
		}
	});
};

// Convertir les [data-src] en [src]
const srcInit = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");

	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
function swiperInit() {
	document.querySelectorAll(".home-slider.swiper").forEach((slider) => {
		if (slider.swiper || slider.querySelectorAll(".swiper-slide").length < 2) return;
		new Swiper(slider, {
			// Core parameters
			slidesPerView: 1,
			speed: 1200,
			resizeObserver: true,
			loop: true,
			createElements: true,
			effect: "fade",
			fadeEffect: {
				crossFade: true,
			},

			// Modules
			modules: [Autoplay, EffectFade, Navigation, Pagination, A11y],
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			},
			// Créer les flèches et les puces dans ce slider.
			navigation: {
				auto: true,
			},
			pagination: {
				auto: true,
				type: "bullets",
				clickable: true,
			},
			a11y: {
				prevSlideMessage: "Image précédente",
				nextSlideMessage: "Image suivante",
				paginationBulletMessage: "Afficher l’image {{index}}",
			},
		});
	});

	["home-agenda", "home-news"].forEach((name) => {
		document.querySelectorAll(`.${name}__slider.swiper`).forEach((slider) => {
			if (slider.swiper) return;
			const section = slider.closest(`.${name}`);
			const multiple = slider.querySelectorAll(".swiper-slide").length > 1;
			new Swiper(slider, {
				modules: [Navigation, Pagination, A11y],
				slidesPerView: 1,
				spaceBetween: 20,
				speed: 500,
				resizeObserver: true,
				watchOverflow: true,
				rewind: multiple,
				navigation: name === "home-agenda" && multiple ? {
					prevEl: section.querySelector(`.${name}__prev`),
					nextEl: section.querySelector(`.${name}__next`),
				} : false,
				pagination: multiple ? {
					el: section.querySelector(`.${name}__pagination`),
					type: "bullets",
					clickable: true,
				} : false,
				a11y: {
					prevSlideMessage: "Élément précédent",
					nextSlideMessage: "Élément suivant",
					paginationBulletMessage: "Afficher l’élément {{index}}",
				},
			});
		});
	});
}

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
function initScrollSmooth() {
	if (document.body.classList.contains("elementor-editor-active")) {
		return;
	}

	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
}

// GSAP : Découpage des textes (avec SplitText)
function gsapSplitText() {
	const textsToAnimated = document.querySelectorAll([
		// global
		'[data-animation^="reveal-text"]',
		".elementor-heading-title",
	]);

	textsToAnimated.forEach((_thisText) => {
		SplitText.create(_thisText, {
			tag: "span",
			// type: "lines, words, chars",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
		});
	});
}

// GSAP : Animations au scroll (avec ScrollTrigger)
function gsapAnimations() {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"[data-animation]",
		".elementor-heading-title",
		".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
	]);

	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;

		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",

			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},

			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},

			// markers: true,
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]'
	);

	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;

		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",

			// markers: true,

			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,

						snap: {
							textContent: 1,
						},

						stagger: {
							each: 1.0,

							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll([
		'[data-animation="reveal-text-reading"]',
	]);

	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;

		gsap.fromTo(
			_this.querySelectorAll(".word"),
			{
				opacity: 0.33,
			},
			{
				delay: delay,
				stagger: 0.3,
				ease: "power1.out",
				opacity: 1,

				scrollTrigger: {
					trigger: _this,
					start: "top 88%",
					end: "top 22%",
					scrub: true,

					// markers: true,
				},
			}
		);
	});

	// data-animation="skid"
	const elementsToskid = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="skid"]',
	]);

	elementsToskid.forEach((_this) => {
		gsap.to(_this, {
			scrollTrigger: {
				trigger: _this,
				pin: true,
				pinSpacing: false,
				start: "top top",

				// markers: true,
			},
		});
	});
}

// Actions exécutées après le preLoader d'accueil
function afterPreloader() {
	scrollEnabled(true);
	swiperInit();
	gsapAnimations();
	scrollToAnchor();
}

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
			scrollSmooth = false;
			scrollEnabled(true);
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);
	gsapSplitText();

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();

	loaderTimeline
		// animation
		.to("#page-preloader .preloader__wrapper", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			opacity: 0,
		})
		.set("#page-preloader", {
			visibility: "hidden",
		});

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
};
